import React from 'react';
import { Info, Wallpaper, FileText, Droplet, Zap, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией об обоях и советами по поклейке
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё об обоях: типы, выбор, поклейка
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Wallpaper className="w-5 h-5 text-indigo-500" />
          Типы обоев и их особенности
        </h3>
        <p>
          На российском рынке представлено множество разновидностей обоев, каждая из которых имеет свои преимущества и особенности применения:
        </p>
        <ul>
          <li><strong>Бумажные обои</strong> — самый экономичный вариант, подходят для помещений с нормальной влажностью. Легко клеятся, но недолговечны и могут выгорать.</li>
          <li><strong>Виниловые обои</strong> — имеют повышенную влагостойкость, подходят для кухонь и коридоров. Благодаря рельефной структуре помогают скрыть неровности стен.</li>
          <li><strong>Флизелиновые обои</strong> — изготовлены на основе нетканого материала, не деформируются при намокании, "дышат" и позволяют стенам испарять влагу. Удобны в поклейке, так как клей наносится только на стену.</li>
          <li><strong>Текстильные обои</strong> — обладают отличными звуко- и теплоизоляционными свойствами, создают уютную атмосферу, но требуют бережного ухода и стоят дороже других видов.</li>
          <li><strong>Фотообои</strong> — позволяют создать уникальный акцент в интерьере благодаря фотографическому изображению. Доступны в различных размерах и материалах исполнения.</li>
          <li><strong>Жидкие обои</strong> — представляют собой декоративную штукатурку на основе целлюлозных или шелковых волокон. Наносятся шпателем, образуют бесшовное покрытие, можно обновлять локальные участки.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-500" />
          Стандартные размеры обоев в России
        </h3>
        <p>
          Стандартные размеры рулонов обоев на российском рынке:
        </p>
        <ul>
          <li><strong>Бумажные и виниловые обои:</strong> ширина 53 см, длина 10,05 м</li>
          <li><strong>Флизелиновые обои:</strong> ширина 53 или 106 см, длина 10,05 м</li>
          <li><strong>Метровые обои:</strong> ширина 100 см, длина варьируется от 10 до 25 м</li>
          <li><strong>Фотообои:</strong> размеры зависят от производителя, ширина панелей обычно от 50 до 127 см</li>
        </ul>
        
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Как правильно измерить комнату?</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Измерьте периметр комнаты (сложите длины всех стен) и высоту от пола до потолка. Из общей площади стен вычтите площадь окон и дверей. Добавьте 10-15% на подгонку рисунка и подрезку. При выборе обоев с крупным раппортом добавьте 15-20% на совмещение узора.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Droplet className="w-5 h-5 text-indigo-500" />
          Выбор клея и инструментов
        </h3>
        <p>
          Правильный выбор клея напрямую влияет на качество и долговечность поклейки:
        </p>
        <ul>
          <li><strong>Для бумажных обоев</strong> подойдет обычный клей на основе КМЦ (карбоксиметилцеллюлозы).</li>
          <li><strong>Для тяжелых обоев</strong> (виниловых, текстильных) используйте усиленный клей с добавлением ПВА.</li>
          <li><strong>Для флизелиновых обоев</strong> применяйте специальный клей для флизелиновых обоев — он наносится только на стену.</li>
          <li><strong>Для жидких обоев</strong> клей обычно не требуется, они наносятся непосредственно на подготовленную поверхность.</li>
        </ul>
        <p>
          <strong>Необходимые инструменты:</strong> нож для обоев, валик для нанесения клея, шпатель для разглаживания, уровень, рулетка, карандаш, ванночка для клея, кисть для проклейки углов и стыков.
        </p>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-indigo-500" />
          Технология поклейки обоев
        </h3>
        <ol>
          <li><strong>Подготовка поверхности:</strong> стены должны быть чистыми, сухими, без пыли. Старые обои полностью удалите, трещины зашпаклюйте.</li>
          <li><strong>Грунтовка:</strong> нанесите грунтовку глубокого проникновения для улучшения адгезии и уменьшения расхода клея.</li>
          <li><strong>Разметка стен:</strong> с помощью уровня проведите вертикальную линию от угла комнаты — по ней будете ориентировать первую полосу.</li>
          <li><strong>Нарезка полотен:</strong> отмерьте полосы с запасом 5-10 см сверху и снизу. При наличии раппорта учитывайте подгонку рисунка.</li>
          <li><strong>Нанесение клея:</strong> для бумажных обоев клей наносится на полотно, для флизелиновых — только на стену.</li>
          <li><strong>Поклейка:</strong> первую полосу выравнивайте строго по вертикальной линии, последующие — встык к предыдущим. Разглаживайте полотна от центра к краям.</li>
          <li><strong>Обработка швов:</strong> прикатайте стыки специальным валиком, излишки клея сразу удаляйте влажной губкой.</li>
          <li><strong>Подрезка:</strong> после высыхания аккуратно обрежьте лишние части у пола и потолка острым ножом.</li>
        </ol>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Распространенные ошибки при поклейке обоев
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Недостаточная подготовка стен (непрогрунтованная поверхность, наличие жирных пятен)</li>
            <li>Неверный расчет количества обоев, особенно при наличии узора с раппортом</li>
            <li>Использование неподходящего клея для выбранного типа обоев</li>
            <li>Нарушение технологии нанесения клея (слишком много или мало)</li>
            <li>Наличие сквозняков в помещении во время поклейки и высыхания</li>
            <li>Несоблюдение времени пропитки обоев клеем (для тех типов, которые требуют пропитки)</li>
          </ul>
        </div>

        <h3>Уход за обоями</h3>
        <p>Срок службы обоев зависит не только от качества поклейки, но и от правильного ухода:</p>
        <ul>
          <li><strong>Бумажные обои</strong> не рекомендуется мыть, допустима только сухая чистка мягкой щеткой или пылесосом.</li>
          <li><strong>Виниловые обои</strong> можно протирать влажной тряпкой или губкой с мягким моющим средством.</li>
          <li><strong>Флизелиновые обои</strong> в зависимости от верхнего слоя могут быть моющимися или только для сухой чистки.</li>
          <li><strong>Текстильные обои</strong> рекомендуется чистить только пылесосом с мягкой насадкой.</li>
          <li><strong>Жидкие обои</strong> после полного высыхания можно обновлять локально, нанося новый слой на поврежденные участки.</li>
        </ul>

        <h3>Экологичность и безопасность</h3>
        <p>
          При выборе обоев обращайте внимание на экологическую безопасность материалов, особенно для детских комнат и спален:
        </p>
        <ul>
          <li>Выбирайте обои с маркировкой "экологически чистый продукт" или сертификацией EcoLabel.</li>
          <li>Флизелиновые и бумажные обои обычно более экологичны, чем виниловые, так как позволяют стенам "дышать".</li>
          <li>Обращайте внимание на содержание ПВХ и формальдегида в составе обоев.</li>
          <li>Современные производители предлагают антибактериальные обои, которые препятствуют развитию плесени и бактерий.</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор обоев поможет вам точно рассчитать необходимое количество материалов для ремонта, сэкономить деньги и время!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;